import React, { useCallback, useEffect, useMemo, useRef } from 'react';

import { Citation, MessageModel } from '../types/models';
import { HighlightedText } from './HighlightedText';
import { SearchMatch, parseAssistant } from './message/constants';
import { createMarkdownComponents } from './message/markdownComponents';
import { useMessageClipboard } from './message/hooks/useMessageClipboard';
import { useMessageEdit } from './message/hooks/useMessageEdit';
import { useAudioRecording } from './message/hooks/useAudioRecording';
import { UserMessage } from './message/UserMessage';
import { AssistantMessage } from './message/AssistantMessage';

interface MessageContainerProps {
	isVisible: boolean;
	message: MessageModel;
	handleFeedback: (index: number, value: -1 | 1) => void;
	model: string;
	citations?: Citation[];
	messageIndex: number;
	onEditMessage?: (index: number, newContent: string) => void;
	searchQuery?: string;
	searchMatches?: SearchMatch[];
	currentMatchIndexInMessage?: number;
	isStreaming?: boolean;
}

export const MessageContainer = React.memo(function MessageContainer({
	message,
	handleFeedback,
	isVisible,
	model,
	citations,
	messageIndex,
	onEditMessage,
	searchQuery,
	currentMatchIndexInMessage = -1,
	isStreaming = false,
}: MessageContainerProps) {
	const [menuVisible, setMenuVisible] = React.useState(isVisible);
	const messageContentRef = useRef<HTMLDivElement>(null);

	// Extracted hooks
	const { handleCopy, handleCopyAsMarkdown, handleCopyEvent } =
		useMessageClipboard(messageContentRef);
	const {
		isEditing,
		editedContent,
		setEditedContent,
		editTextareaRef,
		handleEdit,
		handleSaveEdit,
		handleCancelEdit,
		handleEditKeyDown,
	} = useMessageEdit(message.content, messageIndex, onEditMessage);
	const { isRecording, isTranscribing, handleAudioClick } =
		useAudioRecording(setEditedContent);

	// Search highlighting processor
	const matchCounterRef = useRef(0);
	matchCounterRef.current = 0;

	const processChildren = useCallback(
		(node: React.ReactNode, index?: number): React.ReactNode => {
			if (typeof node === 'string' && searchQuery) {
				return (
					<HighlightedText
						key={index}
						text={node}
						searchQuery={searchQuery}
						currentMatchIndex={currentMatchIndexInMessage}
						matchCounterRef={matchCounterRef}
					/>
				);
			}

			if (React.isValidElement(node)) {
				const element = node as React.ReactElement<any>;
				const elementProps = element.props || {};

				if (
					elementProps.className &&
					typeof elementProps.className === 'string' &&
					(elementProps.className.includes('katex') ||
						elementProps.className.includes('math'))
				) {
					return React.cloneElement(element, { key: index });
				}

				return React.cloneElement(
					element,
					{ ...elementProps, key: index },
					React.Children.map(elementProps.children, (child, childIndex) =>
						processChildren(child, childIndex),
					),
				);
			}

			if (Array.isArray(node)) {
				return node.map((item, itemIndex) =>
					processChildren(item, itemIndex),
				);
			}

			return node;
		},
		[searchQuery, currentMatchIndexInMessage],
	);

	// Markdown components (search-aware)
	const components = useMemo(
		() =>
			createMarkdownComponents(
				searchQuery,
				currentMatchIndexInMessage,
				matchCounterRef,
				processChildren,
			),
		[processChildren, searchQuery, currentMatchIndexInMessage],
	);

	// Separate components for assistant analysis section (no search highlighting)
	const analysisProcessChildren = useCallback(
		(node: React.ReactNode, _index?: number): React.ReactNode => node,
		[],
	);
	const analysisComponents = useMemo(
		() =>
			createMarkdownComponents(
				undefined,
				-1,
				matchCounterRef,
				analysisProcessChildren,
			),
		[analysisProcessChildren],
	);

	// Assistant content parsing
	const isAssistant = message?.role !== 'user';
	const parsed = useMemo(
		() =>
			isAssistant
				? parseAssistant(message.content, message.model || model)
				: { final: '', analysis: undefined },
		[isAssistant, message.content, message.model, model],
	);
	const stickyFinalRef = useRef('');
	if (parsed.final) stickyFinalRef.current = parsed.final;

	const assistantDisplayContent = isAssistant ? stickyFinalRef.current || '' : '';
	const assistantAnalysis = isAssistant ? parsed.analysis : undefined;

	useEffect(() => {
		if (isVisible) setMenuVisible(true);
	}, [isVisible]);

	return (
		<div
			className="leading-relaxed w-full mb-7 px-xs flex flex-col items-start overflow-hidden"
			data-message-index={messageIndex}
		>
			{message?.role === 'user' ? (
				<UserMessage
					message={message}
					components={components}
					messageContentRef={messageContentRef}
					handleCopy={handleCopy}
					handleCopyEvent={handleCopyEvent}
					isEditing={isEditing}
					editedContent={editedContent}
					setEditedContent={setEditedContent}
					editTextareaRef={editTextareaRef}
					handleEdit={handleEdit}
					handleSaveEdit={handleSaveEdit}
					handleCancelEdit={handleCancelEdit}
					handleEditKeyDown={handleEditKeyDown}
					isRecording={isRecording}
					isTranscribing={isTranscribing}
					handleAudioClick={handleAudioClick}
				/>
			) : (
				<AssistantMessage
					message={message}
					messageIndex={messageIndex}
					model={model}
					citations={citations}
					isStreaming={isStreaming}
					menuVisible={menuVisible}
					assistantDisplayContent={assistantDisplayContent}
					assistantAnalysis={assistantAnalysis}
					stickyFinal={stickyFinalRef.current}
					parsedFinal={parsed.final}
					components={components}
					analysisComponents={analysisComponents}
					messageContentRef={messageContentRef}
					handleCopy={handleCopy}
					handleCopyAsMarkdown={handleCopyAsMarkdown}
					handleCopyEvent={handleCopyEvent}
					handleFeedback={handleFeedback}
				/>
			)}
		</div>
	);
}, (prevProps, nextProps) => {
	if (prevProps.message.content !== nextProps.message.content) return false;
	if (prevProps.message.role !== nextProps.message.role) return false;
	if (prevProps.message.feedback !== nextProps.message.feedback) return false;
	if (prevProps.isStreaming !== nextProps.isStreaming) return false;
	if (prevProps.isVisible !== nextProps.isVisible) return false;
	if (prevProps.searchQuery !== nextProps.searchQuery) return false;
	if (prevProps.currentMatchIndexInMessage !== nextProps.currentMatchIndexInMessage) return false;
	if (prevProps.model !== nextProps.model) return false;
	if (prevProps.message.model !== nextProps.message.model) return false;
	if (prevProps.citations !== nextProps.citations) return false;
	if (prevProps.message.tool_calls !== nextProps.message.tool_calls) return false;
	if (prevProps.handleFeedback !== nextProps.handleFeedback) return false;
	if (prevProps.onEditMessage !== nextProps.onEditMessage) return false;
	return true;
});
